import { memo, ReactElement, useMemo, useCallback } from "react"
import styled from "styled-components"
import Heading from "components/Typography/Heading"
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"
import { formatPrice } from "utils/formatPrice"
import VariationDisplay from "../VariationDisplay"

export const Title = styled.span`
  display: flex;
  align-items: center;
  padding: 40px 0px 24px 0px;
`

const ChartContainer = styled.div`
  height: 340px;
  width: 100%;
  background-color: #fafafa;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  gap: 8px;
  padding-left: 36px;
`

const TotalContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`

interface MetricsChartsProps {
  title: string
  chartData: App.ChartData
  isPrice?: boolean
}

const MetricsChart = (props: MetricsChartsProps): ReactElement => {
  const { chartData, isPrice, title } = props

  const formatResult = useMemo(() => {
    const totalValue = chartData?.summary.total

    if (isPrice) return formatPrice(totalValue, "BRL")

    return totalValue
  }, [chartData, isPrice])

  const tooltipFormatter = useCallback(
    (value: number | string, name: string) => {
      if (name === "Valor total") {
        const display = isPrice ? formatPrice(value as number, "BRL") : value

        return [display, name] as [string, string]
      }

      return [value, name] as [number | string, string]
    },
    [isPrice]
  )

  const tooltipLabelFormatter = useCallback((label: string) => {
    return "Data: " + label
  }, [])

  return (
    <ChartContainer>
      <HeaderContainer>
        <Heading variant="h6">{title}</Heading>
        <TotalContainer>
          <Heading variant="h5">{formatResult}</Heading>
          <VariationDisplay value={chartData?.summary} isPrice={isPrice} />
        </TotalContainer>
      </HeaderContainer>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={chartData?.dailyResults}
          margin={{
            top: 20,
            right: 20,
            left: 20
          }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="date" tick={{ fontSize: 12 }} height={60} />
          <YAxis />
          <Tooltip formatter={tooltipFormatter} labelFormatter={tooltipLabelFormatter} />
          <Line dataKey="total" stroke="#5D5FEF" name={"Valor total"} activeDot={{ r: 8 }} />
        </LineChart>
      </ResponsiveContainer>
    </ChartContainer>
  )
}

export default memo(MetricsChart)
